import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCheck, Calendar, DollarSign, Users, Info, FileText, ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import { useLocation } from "wouter";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useListNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, getListNotificationsQueryKey } from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { getNotificationActionRoute } from "@/lib/notificationRoutes";

const typeConfig: Record<string, { icon: React.ElementType; bg: string; text: string }> = {
  appointment: { icon: Calendar, bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-500" },
  estimate:    { icon: DollarSign, bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-500" },
  estimate_submission: { icon: DollarSign, bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-500" },
  estimate_update: { icon: DollarSign, bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-500" },
  proposal:    { icon: FileText, bg: "bg-purple-50 dark:bg-purple-900/20", text: "text-purple-600" },
  proposal_received: { icon: FileText, bg: "bg-purple-50 dark:bg-purple-900/20", text: "text-purple-600" },
  "care-team": { icon: Users, bg: "bg-violet-50 dark:bg-violet-900/20", text: "text-violet-500" },
  careteam:    { icon: Users, bg: "bg-violet-50 dark:bg-violet-900/20", text: "text-violet-500" },
  group_invitation: { icon: Users, bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-500" },
  id_card:     { icon: ShieldCheck, bg: "bg-cyan-50 dark:bg-cyan-900/20", text: "text-cyan-600" },
  default:     { icon: Info, bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-400" },
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ProviderNotifications() {
  const [, setLocation] = useLocation();
  const { data: notifications, isLoading } = useListNotifications({ query: { queryKey: getListNotificationsQueryKey() } });
  const queryClient = useQueryClient();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      markRead.mutate(notification.id, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() })
      });
    }
    const route = getNotificationActionRoute(notification, 'PROVIDER');
    if (route.path) {
      setLocation(route.path);
    }
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate(undefined, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() })
    });
  };

  const unread = notifications?.filter(n => !n.read).length ?? 0;

  return (
    <ProviderLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <Bell className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Notifications</h1>
              <p className="text-slate-400 text-sm mt-0.5">
                {unread > 0 ? `${unread} new update${unread > 1 ? "s" : ""}` : "You're all caught up!"}
              </p>
            </div>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="text-primary gap-1.5 rounded-full h-8 px-3 text-xs hover:bg-primary/10"
              data-testid="button-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 rounded-3xl p-4 flex gap-4 shadow-sm">
                <Skeleton className="w-11 h-11 rounded-2xl flex-shrink-0" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-48 mb-2" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        ) : (notifications?.length ?? 0) === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl py-20 text-center shadow-sm border border-slate-100 dark:border-slate-800">
            <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell className="w-7 h-7 text-slate-300 dark:text-slate-600" />
            </div>
            <p className="font-bold text-slate-800 dark:text-white mb-1">No notifications</p>
            <p className="text-slate-400 text-sm">We'll let you know when something comes up.</p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {notifications?.map((n, i) => {
                const cfg = typeConfig[n.type] ?? typeConfig.default;
                const Icon = cfg.icon;
                const actionRoute = getNotificationActionRoute(n, 'PROVIDER');

                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => handleNotificationClick(n)}
                    className={`group bg-white dark:bg-slate-900 rounded-2xl p-4 flex gap-4 shadow-sm border transition-all cursor-pointer hover:shadow-md hover:border-primary/40 ${
                      n.read
                        ? "border-slate-100/80 dark:border-slate-800/80 opacity-75"
                        : "border-primary/30 dark:border-primary/30 bg-primary/[0.02]"
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                      <Icon className={`w-5 h-5 ${cfg.text}`} />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug ${n.read ? "text-slate-700 dark:text-slate-300" : "font-semibold text-slate-900 dark:text-white"}`}>
                            {n.title}
                          </p>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-slate-400">{timeAgo(n.createdAt)}</span>
                            {!n.read && <div className="w-2 h-2 rounded-full bg-primary" />}
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{n.message}</p>
                      </div>

                      {/* Action Link Footer */}
                      <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs font-semibold text-primary group-hover:underline">
                        <span className="flex items-center gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5" />
                          {actionRoute.label}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </AnimatePresence>
        )}
      </div>
    </ProviderLayout>
  );
}
